// Fire-and-forget client-side telemetry.
//
// The backend endpoint POSTs are best-effort — we never block gameplay
// on network. `navigator.sendBeacon` is preferred where available (works
// even if the tab is closing), with a `fetch(..., { keepalive: true })`
// fallback for older browsers.
//
// Currently only used by the level-transition watchdog site in
// useGameEngine.js — if a player ever hits the stalled-transition path
// in production, we get a server-side event to prove it (and by absence
// of events, conclusively prove the transition pipeline is rock-solid).

import { API_BASE } from './backendUrl';

/**
 * Report that the level-transition watchdog fired.
 *
 * @param {Object} opts
 * @param {number} opts.level          Level the player was on
 * @param {number} opts.elapsedMs      ms from levelTransitionStart to trip
 * @param {string} [opts.walletAddress] Wallet id, or 'guest' when not connected
 * @param {boolean} [opts.multiplayer]  Was this a multiplayer session?
 * @param {boolean} [opts.isHost]       Was this client the host?
 */
export function reportTransitionWatchdog({ level, elapsedMs, walletAddress, multiplayer = false, isHost = false }) {
    const payload = JSON.stringify({
        level: Math.max(1, Math.min(200, Math.floor(level || 1))),
        elapsed_ms: Math.max(0, Math.min(60_000, Math.floor(elapsedMs || 0))),
        wallet_address: (walletAddress || 'guest').slice(0, 64),
        user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 256),
        multiplayer: !!multiplayer,
        is_host: !!isHost,
    });

    const url = `${API_BASE}/telemetry/transition-watchdog`;

    // Prefer sendBeacon — survives page unload.
    try {
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            const ok = navigator.sendBeacon(url, blob);
            if (ok) return;
        }
    } catch (_) { /* fall through to fetch */ }

    // Fallback: fire-and-forget fetch.
    try {
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
        }).catch(() => { /* swallow — best-effort */ });
    } catch (_) { /* swallow — never block gameplay on telemetry */ }
}
